import { aggregate } from './query.js';
export class FormulaError extends Error {
}
function lex(src) {
    const out = [];
    let i = 0;
    while (i < src.length) {
        const ch = src[i];
        if (/\s/.test(ch)) {
            i++;
            continue;
        }
        if (/[0-9.]/.test(ch)) {
            const m = src.slice(i).match(/^\d*\.?\d+(e[+-]?\d+)?/i);
            out.push({ k: 'num', v: m[0] });
            i += m[0].length;
            continue;
        }
        if (ch === '"' || ch === "'") {
            let j = i + 1, s = '';
            while (j < src.length && src[j] !== ch) {
                if (src[j] === '\\')
                    j++;
                s += src[j++];
            }
            if (j >= src.length)
                throw new FormulaError('닫히지 않은 문자열');
            out.push({ k: 'str', v: s });
            i = j + 1;
            continue;
        }
        if (/[A-Za-z_가-힣$]/.test(ch)) {
            const m = src.slice(i).match(/^[A-Za-z_가-힣$][\w가-힣$.]*/);
            out.push({ k: 'id', v: m[0] });
            i += m[0].length;
            continue;
        }
        const two = src.slice(i, i + 2);
        if (['<=', '>=', '!=', '<>', '==', '&&', '||'].includes(two)) {
            out.push({ k: 'op', v: two });
            i += 2;
            continue;
        }
        if ('+-*/%^&=<>!(),?:'.includes(ch)) {
            out.push({ k: 'op', v: ch });
            i++;
            continue;
        }
        throw new FormulaError(`알 수 없는 문자 '${ch}'`);
    }
    out.push({ k: 'end', v: '' });
    return out;
}
// ───────────── 파서 (우선순위 상승) ─────────────
const PREC = { '||': 1, OR: 1, '&&': 2, AND: 2, '=': 3, '==': 3, '!=': 3, '<>': 3, '<': 4, '<=': 4, '>': 4, '>=': 4, '&': 5, '+': 6, '-': 6, '*': 7, '/': 7, '%': 7, '^': 8 };
export function parseFormula(src) {
    const toks = lex(src);
    let p = 0;
    const peek = () => toks[p];
    const take = () => toks[p++];
    const expect = (v) => { const t = take(); if (t.v !== v)
        throw new FormulaError(`'${v}' 가 필요합니다 (위치 ${p})`); };
    const opOf = (t) => (t.k === 'op' ? t.v : t.k === 'id' && /^(and|or)$/i.test(t.v) ? t.v.toUpperCase() : null);
    function primary() {
        const t = take();
        if (t.k === 'num')
            return { t: 'num', v: Number(t.v) };
        if (t.k === 'str')
            return { t: 'str', v: t.v };
        if (t.k === 'op' && t.v === '(') {
            const e = ternary();
            expect(')');
            return e;
        }
        if (t.k === 'op' && (t.v === '-' || t.v === '+' || t.v === '!'))
            return { t: 'un', op: t.v, a: unary() };
        if (t.k === 'id') {
            const u = t.v.toUpperCase();
            if (u === 'NOT')
                return { t: 'un', op: '!', a: unary() };
            if (u === 'TRUE')
                return { t: 'bool', v: true };
            if (u === 'FALSE')
                return { t: 'bool', v: false };
            if (u === 'NULL')
                return { t: 'null' };
            if (u === 'CURROW')
                return { t: 'currow' };
            if (peek().v === '(') {
                take();
                const args = [];
                if (peek().v !== ')') {
                    args.push(ternary());
                    while (peek().v === ',') {
                        take();
                        args.push(ternary());
                    }
                }
                expect(')');
                return { t: 'call', fn: u, args };
            }
            // XPlatform 식: dataset.getColumn(currow,'x') 대신 컬럼 이름을 그대로 쓴다. "ds.col" 형태면 마지막 이름
            return { t: 'col', id: t.v.includes('.') ? t.v.slice(t.v.lastIndexOf('.') + 1) : t.v };
        }
        throw new FormulaError(`예상하지 못한 '${t.v || '끝'}'`);
    }
    function unary() { return primary(); }
    function binary(minPrec) {
        let left = unary();
        for (;;) {
            const op = opOf(peek());
            const pr = op ? PREC[op] : undefined;
            if (pr == null || pr < minPrec)
                return left;
            take();
            const right = binary(op === '^' ? pr : pr + 1);
            left = { t: 'bin', op: op, a: left, b: right };
        }
    }
    function ternary() {
        const c = binary(1);
        if (peek().v === '?') {
            take();
            const a = ternary();
            expect(':');
            const b = ternary();
            return { t: 'tern', c, a, b };
        }
        return c;
    }
    const e = ternary();
    if (peek().k !== 'end')
        throw new FormulaError(`남은 내용 '${peek().v}'`);
    return e;
}
const num = (v) => (v == null || v === '' ? NaN : typeof v === 'number' ? v : typeof v === 'boolean' ? (v ? 1 : 0) : Number(String(v).replace(/,/g, '')));
const truthy = (v) => !(v == null || v === false || v === 0 || v === '' || (typeof v === 'number' && isNaN(v)));
const eq = (a, b) => (a == null || a === '') && (b == null || b === '') ? true : typeof a === 'number' || typeof b === 'number' ? num(a) === num(b) : String(a) === String(b);
const toDate = (v) => { if (v == null || v === '')
    return null; const s = String(v); const m = s.match(/^(\d{4})-?(\d{2})-?(\d{2})/); return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null; };
const fmtDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export function compileFormula(src) {
    return compile(parseFormula(src));
}
function compile(n) {
    switch (n.t) {
        case 'num':
        case 'str':
        case 'bool': {
            const v = n.v;
            return () => v;
        }
        case 'null': return () => null;
        case 'currow': return c => c.row;
        case 'col': {
            const id = n.id;
            return c => c.ds.get(c.row, id);
        }
        case 'un': {
            const a = compile(n.a);
            return n.op === '-' ? c => -num(a(c)) : n.op === '+' ? c => num(a(c)) : c => !truthy(a(c));
        }
        case 'tern': {
            const cc = compile(n.c), a = compile(n.a), b = compile(n.b);
            return c => (truthy(cc(c)) ? a(c) : b(c));
        }
        case 'bin': {
            const a = compile(n.a), b = compile(n.b);
            switch (n.op) {
                case '+': return c => { const x = a(c), y = b(c); return typeof x === 'string' && isNaN(num(x)) || typeof y === 'string' && isNaN(num(y)) ? `${x ?? ''}${y ?? ''}` : num(x) + num(y); };
                case '-': return c => num(a(c)) - num(b(c));
                case '*': return c => num(a(c)) * num(b(c));
                case '/': return c => { const d = num(b(c)); return d === 0 ? null : num(a(c)) / d; };
                case '%': return c => num(a(c)) % num(b(c));
                case '^': return c => Math.pow(num(a(c)), num(b(c)));
                case '&': return c => `${a(c) ?? ''}${b(c) ?? ''}`;
                case '=':
                case '==': return c => eq(a(c), b(c));
                case '!=':
                case '<>': return c => !eq(a(c), b(c));
                case '<': return c => cmp(a(c), b(c)) < 0;
                case '<=': return c => cmp(a(c), b(c)) <= 0;
                case '>': return c => cmp(a(c), b(c)) > 0;
                case '>=': return c => cmp(a(c), b(c)) >= 0;
                case '&&':
                case 'AND': return c => truthy(a(c)) && truthy(b(c));
                case '||':
                case 'OR': return c => truthy(a(c)) || truthy(b(c));
            }
            throw new FormulaError('연산자 ' + n.op);
        }
        case 'call': return compileCall(n.fn, n.args);
    }
}
function cmp(x, y) {
    const a = num(x), b = num(y);
    if (!isNaN(a) && !isNaN(b))
        return a - b;
    return String(x ?? '').localeCompare(String(y ?? ''), 'ko');
}
function compileCall(fn, argNodes) {
    // 집계 함수: 인자가 컬럼 하나면 뷰 전체 대상
    if (['SUM', 'AVG', 'COUNT', 'MIN', 'MAX'].includes(fn) && argNodes.length === 1 && argNodes[0].t === 'col') {
        const id = argNodes[0].id, agg = fn.toLowerCase();
        return c => (c.rows ? aggregate(c.ds, c.rows, id, agg) : c.ds.get(c.row, id));
    }
    const args = argNodes.map(compile);
    const A = (c) => args.map(f => f(c));
    switch (fn) {
        case 'IF':
        case 'IIF': return c => (truthy(args[0](c)) ? args[1]?.(c) ?? null : args[2]?.(c) ?? null);
        case 'DECODE': return c => {
            const x = args[0](c);
            for (let i = 1; i + 1 < args.length; i += 2)
                if (eq(x, args[i](c)))
                    return args[i + 1](c);
            return args.length % 2 === 0 ? args[args.length - 1](c) : null;
        };
        case 'AND': return c => args.every(f => truthy(f(c)));
        case 'OR': return c => args.some(f => truthy(f(c)));
        case 'NOT': return c => !truthy(args[0](c));
        case 'ISNULL': return c => { const v = args[0](c); return v == null || v === ''; };
        case 'NVL':
        case 'COALESCE': return c => { for (const f of args) {
            const v = f(c);
            if (v != null && v !== '')
                return v;
        } return null; };
        case 'ROUND': return c => { const v = num(args[0](c)), d = args[1] ? num(args[1](c)) : 0; if (isNaN(v))
            return null; const k = Math.pow(10, d); return Math.round(v * k + Math.sign(v) * 1e-9) / k; };
        case 'TRUNC': return c => { const v = num(args[0](c)), d = args[1] ? num(args[1](c)) : 0, k = Math.pow(10, d); return isNaN(v) ? null : Math.trunc(v * k) / k; };
        case 'FLOOR': return c => Math.floor(num(args[0](c)));
        case 'CEIL': return c => Math.ceil(num(args[0](c)));
        case 'ABS': return c => Math.abs(num(args[0](c)));
        case 'MIN': return c => { const v = A(c).map(num).filter(x => !isNaN(x)); return v.length ? Math.min(...v) : null; };
        case 'MAX': return c => { const v = A(c).map(num).filter(x => !isNaN(x)); return v.length ? Math.max(...v) : null; };
        case 'SUM': return c => A(c).map(num).filter(x => !isNaN(x)).reduce((a, b) => a + b, 0);
        case 'AVG': return c => { const v = A(c).map(num).filter(x => !isNaN(x)); return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null; };
        case 'COUNT': return c => A(c).filter(v => v != null && v !== '').length;
        case 'LEN': return c => String(args[0](c) ?? '').length;
        case 'LEFT': return c => String(args[0](c) ?? '').slice(0, num(args[1](c)));
        case 'RIGHT': return c => { const s = String(args[0](c) ?? ''); return s.slice(Math.max(0, s.length - num(args[1](c)))); };
        case 'MID': return c => String(args[0](c) ?? '').substr(num(args[1](c)) - 1, args[2] ? num(args[2](c)) : undefined);
        case 'UPPER': return c => String(args[0](c) ?? '').toUpperCase();
        case 'LOWER': return c => String(args[0](c) ?? '').toLowerCase();
        case 'TRIM': return c => String(args[0](c) ?? '').trim();
        case 'CONCAT': return c => A(c).map(v => v ?? '').join('');
        case 'REPLACE': return c => String(args[0](c) ?? '').split(String(args[1](c))).join(String(args[2](c) ?? ''));
        case 'CONTAINS': return c => String(args[0](c) ?? '').includes(String(args[1](c) ?? ''));
        case 'TEXT': return c => { const v = args[0](c); const d = args[1] ? num(args[1](c)) : null; return v == null ? '' : d != null && !isNaN(num(v)) ? num(v).toFixed(d) : String(v); };
        case 'VALUE': return c => num(args[0](c));
        case 'TODAY': return () => fmtDate(new Date());
        case 'NOW': return () => new Date().toISOString();
        case 'YEAR': return c => toDate(args[0](c))?.getFullYear() ?? null;
        case 'MONTH': return c => { const d = toDate(args[0](c)); return d ? d.getMonth() + 1 : null; };
        case 'DAY': return c => toDate(args[0](c))?.getDate() ?? null;
        case 'DATEDIFF': return c => { const a = toDate(args[0](c)), b = toDate(args[1](c)); return a && b ? Math.round((b.getTime() - a.getTime()) / 86400000) : null; };
        case 'ADDDAYS': return c => { const d = toDate(args[0](c)); if (!d)
            return null; d.setDate(d.getDate() + num(args[1](c))); return fmtDate(d); };
    }
    throw new FormulaError(`지원하지 않는 함수 ${fn}()`);
}
/** 컬럼 수식 → GridColumn.value 로 쓸 함수 */
export function formulaColumn(src) {
    const f = compileFormula(src);
    return (row, ds) => { const v = f({ ds, row }); return typeof v === 'number' && !isFinite(v) ? null : v; };
}
/** 집계 수식 (합계 행 등): SUM(amt)/COUNT(ordCd) */
export function evalAggregate(src, ds, rows) {
    const v = compileFormula(src)({ ds, row: rows.length ? rows[0] : 0, rows });
    return typeof v === 'number' && !isFinite(v) ? null : v;
}
